// work/proposals/redlining/_schemas/proposal-field-schema.ts
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import { z } from "zod";

export type ProposalFieldJSON = z.infer<typeof ProposalFieldSchema.ProposalFieldJSONZ>;
export type ProposalFieldParams = z.infer<typeof ProposalFieldSchema.ProposalFieldZ>;

export default class ProposalFieldSchema {
  public static readonly ProposalFieldJSONZ = z.union([
    z.object({
      get name() {
        return z.enum(Object.values(ProposalFieldName));
      },
      get category() {
        return z.enum(Object.values(ProposalFieldCategory));
      },
      id: z.uuid().nullish(),
      get commentFieldOverride() {
        return ProposalFieldSchema.ProposalFieldJSONZ.nullish();
      },
    }),
    z.literal("general"),
  ]);

  public static readonly ProposalFieldZ = z.union([
    z.object({
      get name() {
        return z.enum(Object.values(ProposalFieldName));
      },
      get category() {
        return z.enum(Object.values(ProposalFieldCategory));
      },
      id: z.instanceof(Guid).optional(),
      get commentFieldOverride() {
        return z.instanceof(ProposalField).optional();
      },
    }),
    z.templateLiteral(["general"]),
  ]);

  public static validate(values: unknown): ProposalFieldParams {
    return parseOrThrow(ProposalFieldSchema.ProposalFieldZ, values, {
      logError: true,
      schemaName: "ProposalFieldSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ProposalFieldJSON {
    return parseOrThrow(ProposalFieldSchema.ProposalFieldJSONZ, values, {
      logError: true,
      schemaName: "ProposalFieldSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalFieldParams {
    try {
      ProposalFieldSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ProposalFieldJSON {
    try {
      ProposalFieldSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: unknown): ProposalFieldParams {
    const parsed = parseOrThrow(ProposalFieldSchema.ProposalFieldJSONZ, data, {
      logError: true,
      schemaName: "ProposalFieldSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ProposalFieldSchema.codec.decode(parsed);
  }

  public static serialize(params: ProposalFieldParams): ProposalFieldJSON {
    const parsed = ProposalFieldSchema.validate(params);
    return ProposalFieldSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(ProposalFieldSchema.ProposalFieldJSONZ, ProposalFieldSchema.ProposalFieldZ, {
    decode: (data: ProposalFieldJSON) =>
      data === "general"
        ? ProposalField.General
        : {
            name: data.name,
            category: data.category,
            id: Guid.fromJSON(data.id) ?? undefined,
            commentFieldOverride: ProposalField.fromJSON(data.commentFieldOverride) ?? undefined,
          },
    encode: (params: ProposalFieldParams) =>
      params === "general"
        ? "general"
        : {
            name: params.name,
            category: params.category,
            id: params.id?.toJSON() ?? null,
            commentFieldOverride: params.commentFieldOverride?.toJSON() ?? null,
          },
  });
}
